import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import type HotelSettingsSpiderSpiderDashboardController from 'lumen/controllers/hotel/settings/spider/spider-dashboard';
import type HotelSettingsIndexRoute from 'lumen/routes/hotel/settings/index';
import type { Transition } from 'lumen/services/transition';
import protectedRoute from 'lumen/utils/decorators/protected-route';
import type { ModelFrom } from 'lumen/utils/routes';
import { HOTEL_SETTINGS_TABS } from 'lumen/utils/spider';

type Model = ModelFrom<HotelSettingsIndexRoute>;

type Params = {
  path: string;
};

@protectedRoute({ features: ['spider.settings.edit'] })
export default class HotelSettingsSpiderSpiderDashboardRoute extends Route {
  @service declare access: Services['access'];
  @service declare navigation: Services['navigation'];
  @service declare router: Services['router'];

  dashboard: string | undefined = undefined;

  get extendedRouteName(): string {
    const { path } = this.paramsFor(
      'hotel.settings.spider.spider-dashboard',
    ) as {
      path: string;
    };

    return this.fullRouteName.replace('spider-dashboard', path);
  }

  beforeModel(transition: Transition): void {
    const { path } = this.paramsFor(
      'hotel.settings.spider.spider-dashboard',
    ) as {
      path: string;
    };

    if (path === 'budget') {
      this.router.replaceWith('hotel.settings.budget');
      return;
    }

    if (path === 'user-forecast') {
      this.router.replaceWith('hotel.settings.forecast');
      return;
    }

    if (this.navigation.redirectToAccountSelection(transition)) {
      return;
    }

    const availablePaths = HOTEL_SETTINGS_TABS.reduce((accumulator, tab) => {
      if (this.access.hasFeature(tab.featureFlag)) {
        accumulator.push(tab.path);
      }

      return accumulator;
    }, [] as string[]);

    if (!availablePaths.includes(path)) {
      this.router.replaceWith(
        'hotel.settings.spider.spider-dashboard',
        availablePaths[0],
      );
    }
  }

  model(params: Params): Model {
    /**
     * Stored `path` as a class property, because returning it from
     * the `model` hook will trigger a refresh when `path` changes.
     *
     * Spider handles changing routes. It will send a message to
     * update the URL.
     */
    this.dashboard = params.path;

    return this.modelFor('hotel.settings');
  }

  setupController(
    controller: HotelSettingsSpiderSpiderDashboardController,
  ): void {
    // @ts-expect-error: A spread argument must either have a tuple type or be passed to a rest parameter.
    // eslint-disable-next-line prefer-rest-params
    super.setupController(...arguments);

    controller.dashboard = this.dashboard;
  }
}
